import React, { useContext, useState, useEffect } from "react";
import { Modal, TextInput, Label, Select } from "flowbite-react";
import Swal from "sweetalert2";
import axios from "axios";
import DataTable from "react-data-table-component";
import engineGif from "../image/engine.gif";
import Button_submit from "../components/Button_submit";
import GlobalContext from "../connections/GlobalContext";
import Head_data from "../components/Head_data";
import jsPDF from "jspdf";
import Logo from "../image/logo.jpg";
import html2canvas from "html2canvas";

const Expenses = ({ theme }) => {
  const { globalValue } = useContext(GlobalContext);

  // Main states
  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("add");

  // Payment management states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editPaymentModalOpen, setEditPaymentModalOpen] = useState(false);
  const [paymentDetailsModalOpen, setPaymentDetailsModalOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    expense_id: "",
    amount: "",
    remark: "",
    date: "",
    payment_type: "",
  });
  const [editPaymentData, setEditPaymentData] = useState({
    payment_id: "",
    amount: "",
    remark: "",
    date: "",
    payment_type: "",
  });
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [temp_total, setTempTotal] = useState(0);

  // Form and UI states
  const [formData, setFormData] = useState({
    id: null,
    category_id: "",
    amount: "",
    date: "",
    staffs: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [pdfpopup, setPdfpopup] = useState(false);
  const [pdf_data, setPdfData] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [categoryExpenseNames, setCategoryExpenseNames] = useState([]);

  // Helper functions
  const getTodayDate = () => {
    const today = new Date();
    const yyyy = today.getFullYear();
    let mm = today.getMonth() + 1;
    let dd = today.getDate();
    if (dd < 10) dd = '0' + dd;
    if (mm < 10) mm = '0' + mm;
    return `${yyyy}-${mm}-${dd}`;
  };

  const convertToYYYYMMDD = (dateStr) => {
    if (!dateStr) return getTodayDate();
    try {
      if (dateStr.includes('/')) {
        const [dd, mm, yyyy] = dateStr.split('/');
        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
      }
      return dateStr;
    } catch {
      return getTodayDate();
    }
  };

  // Data fetching functions
  const fetchExpenses = () => {
    setLoading(true);
    const branch = sessionStorage.getItem("branch");

    axios
      .get(`${globalValue}/expense_display`, { params: { branch } })
      .then((res) => {
        const processedExpenses = (Array.isArray(res.data) ? res.data : []).map(expense => ({
          ...expense,
          paid_amount: parseFloat(expense.paid_amount || 0),
          pending_amount: parseFloat(expense.pending_amount || 0),
          payment_entries: Array.isArray(expense.payment_entries) ? expense.payment_entries : []
        }));
        setExpenses(processedExpenses);
        setFilteredExpenses(processedExpenses);
      })
      .catch((err) => {
        console.error(err);
        setExpenses([]);
        setFilteredExpenses([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchCategories = () => {
    setLoading(true);
    axios
      .get(`${globalValue}/category_display`)
      .then((res) => {
        setCategories(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error(err);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${globalValue}/staffs`, {
        params: {
          data: sessionStorage.getItem("branch"),
          position: "",
        },
        headers: {
          "x-access-token": sessionStorage.getItem("token"),
        },
      });

      const formatted = (response.data || []).map((s) => ({
        id: s[0],
        code: s[1],
        name: s[2],
        phone: s[3],
        address: s[4],
        username: s[5],
        roles: s[6],
        branch: s[7],
        email: s[8],
      }));
      setStaffList(formatted);
    } catch (err) {
      console.error('Error fetching staff list:', err);
      setStaffList([]);
    }
  };

  const fetchPaymentDetails = async (expenseId) => {
    try {
      const res = await axios.get(`${globalValue}/expensePaymentDetails/${expenseId}`);
      const expense = expenses.find(exp => exp.id == expenseId);
      setSelectedExpense(expense || null);
      const apiData = res.data;
      let entries = [];
      if (Array.isArray(apiData)) {
        entries = apiData;
      } else if (apiData && Array.isArray(apiData.payment_entries)) {
        entries = apiData.payment_entries;
      } else if (Array.isArray(expense?.payment_entries)) {
        entries = expense.payment_entries;
      }
      const normalized = entries.map((e, i) => ({
        payment_id: e.payment_id ?? e.id ?? i + 1,
        amount: parseFloat(e.amount || 0),
        remark: e.remark || "",
        date: e.date || "",
        payment_type: e.payment_type || "",
      }));
      setPaymentDetails(normalized);
    } catch (err) {
      console.error('Error fetching payment details:', err);
      const expense = expenses.find(exp => exp.id == expenseId);
      setSelectedExpense(expense || null);
      const fallback = Array.isArray(expense?.payment_entries) ? expense.payment_entries : [];
      const normalized = fallback.map((e, i) => ({
        payment_id: e.payment_id ?? e.id ?? i + 1,
        amount: parseFloat(e.amount || 0),
        remark: e.remark || "",
        date: e.date || "",
        payment_type: e.payment_type || "",
      }));
      setPaymentDetails(normalized);
    }
    setPaymentDetailsModalOpen(true);
  };

  // Effects
  useEffect(() => {
    fetchExpenses();
    fetchCategories();
    setFormData(prev => ({ ...prev, date: getTodayDate() }));
  }, []);

  useEffect(() => {
    const filtered = (Array.isArray(expenses) ? expenses : []).filter(
      (row) =>
        (row.category_name && row.category_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (row.staffs && row.staffs.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    setFilteredExpenses(filtered);
  }, [searchTerm, expenses]);

  useEffect(() => {
    if (formData.category_id) {
      const categoryName = categories.find(c => c.id === Number(formData.category_id))?.category_name;

      if (categoryName?.toLowerCase().includes("salary staff")) {
        fetchStaffList();
        setCategoryExpenseNames([]);
      } else {
        const uniqueNames = [
          ...new Set(
            (Array.isArray(filteredExpenses) ? filteredExpenses : [])
              .filter(item => item.category_id === Number(formData.category_id))
              .map(item => item.staffs)
              .filter(Boolean)
          ),
        ];
        setCategoryExpenseNames(uniqueNames);
      }
    }
  }, [formData.category_id, expenses, categories, filteredExpenses]);

  // CRUD Operations
  const handleSubmit = async () => {
    if (!formData.category_id || !formData.amount || !formData.date || !formData.staffs) {
      Swal.fire("Error", "All fields are required", "error");
      return;
    }

    const url = formData.id ? `${globalValue}/expense_edit` : `${globalValue}/expense_add`;
    setLoading(true);

    try {
      const payload = { ...formData, branch: sessionStorage.getItem("branch") };
      const res = await axios.post(url, payload);

      if (res.data.success === 1) {
        Swal.fire("Success", res.data.message, "success");
        fetchExpenses();
        setModalOpen(false);
        setFormData({
          id: null,
          category_id: "",
          amount: "",
          date: getTodayDate(),
          staffs: "",
        });
      } else {
        Swal.fire("Error", res.data.message, "error");
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setFormData({
      id: row.id,
      category_id: row.category_id,
      amount: row.amount,
      date: row.date?.includes("/") ? row.date.split("/").reverse().join("-") : row.date,
      staffs: row.staffs,
    });
    setMode("edit");
    setModalOpen(true);
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.post(`${globalValue}/expense_delete`, { id })
          .then((res) => {
            if (res.data.success === 1) {
              Swal.fire("Deleted!", res.data.message, "success");
              fetchExpenses();
            } else {
              Swal.fire("Error", res.data.message, "error");
            }
          })
          .catch((err) => Swal.fire("Error", err.message, "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  // Payment handlers
  const handlePaymentSubmit = async () => {
    if (!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0) {
      Swal.fire("Error", "Amount must be greater than 0", "error");
      return;
    }
    if (!paymentFormData.date || !paymentFormData.payment_type) {
      Swal.fire("Error", "Date  or payment type are required", "error");
      return;
    }

    const pendingAmount = parseFloat(selectedExpense?.pending_amount || 0);
    const paymentAmount = parseFloat(paymentFormData.amount);

    if (paymentAmount > pendingAmount) {
      Swal.fire("Error", `Payment cannot exceed pending amount: Rs. ${pendingAmount.toLocaleString()}`, "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        expense_id: parseInt(selectedExpense.id),
        amount: paymentAmount,
        remark: paymentFormData.remark || "",
        date: paymentFormData.date,
        payment_type: paymentFormData.payment_type || "",
      };

      const res = await axios.post(`${globalValue}/payExpense`, payload);
      if (res.data.success === 1) {
        Swal.fire("Success", "Payment recorded successfully", "success");
        setPaymentModalOpen(false);
        setPaymentDetailsModalOpen(false);
        setPaymentFormData({ expense_id: "", amount: "", remark: "", date: getTodayDate(), payment_type: "" });
        fetchExpenses();
      } else {
        Swal.fire("Error", res.data.message || "Failed to record payment", "error");
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to record payment", "error");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = (expense) => {
    setSelectedExpense(expense);
    setPaymentFormData({
      expense_id: expense.id,
      amount: "",
      remark: "",
      date: getTodayDate(),
      payment_type: "",
    });
    setPaymentModalOpen(true);
  };

  const handleEditPayment = (payment) => {
    setEditPaymentData({
      payment_id: payment.payment_id || payment.id,
      amount: payment.amount,
      remark: payment.remark || "",
      date: convertToYYYYMMDD(payment.date),
      payment_type: payment.payment_type || "",
    });
    setEditPaymentModalOpen(true);
  };

  const handleUpdatePayment = async () => {
    if (!editPaymentData.amount || parseFloat(editPaymentData.amount) <= 0) {
      Swal.fire("Error", "Amount must be greater than 0", "error");
      return;
    }
    if (!editPaymentData.date || !editPaymentData.payment_type) {
      Swal.fire("Error", "Date  or payment type are required", "error");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        payment_id: parseInt(editPaymentData.payment_id),
        amount: parseFloat(editPaymentData.amount),
        remark: editPaymentData.remark || "",
        date: editPaymentData.date,
        payment_type: editPaymentData.payment_type || "",
      };
      const res = await axios.put(`${globalValue}/editExpensePayment`, payload);
      if (res.data.success === 1) {
        Swal.fire("Success", "Payment updated successfully", "success");
        setEditPaymentModalOpen(false);
        setPaymentModalOpen(false);
        setPaymentDetailsModalOpen(false);
        setEditPaymentData({ payment_id: "", amount: "", remark: "", date: "" });
        fetchExpenses();
      }
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Failed to update payment", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePayment = (paymentId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        axios.delete(`${globalValue}/deleteExpensePayment/${paymentId}`)
          .then((res) => {
            if (res.data.success === 1) {
              Swal.fire("Deleted!", "Payment deleted successfully", "success");
              setPaymentDetailsModalOpen(false);
              setEditPaymentModalOpen(false);
              setPaymentModalOpen(false);
              fetchExpenses();
            } else {
              Swal.fire("Error", res.data.message || "Failed to delete payment", "error");
            }
          })
          .catch((err) => Swal.fire("Error", err.message, "error"))
          .finally(() => setLoading(false));
      }
    });
  };

  // PDF functions
  const openPdfModal = (row) => {
    setPdfData(row);
    setPdfpopup(true);
  };

  const handlePrint = () => {
    const content = document.getElementById("pdf-content");
    if (!content) return;

    html2canvas(content, { scale: 2, useCORS: true }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
      let remainingHeight = pdfHeight - pdf.internal.pageSize.getHeight();
      while (remainingHeight > 0) {
        position -= pdf.internal.pageSize.getHeight();
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        remainingHeight -= pdf.internal.pageSize.getHeight();
      }

      pdf.save(`Expense_${pdf_data?.id || "receipt"}.pdf`);
      setPdfpopup(false);
    });
  };

  // Enhanced columns with status badges
  const columns = [
    { name: "S.No", selector: (row) => row.sno, sortable: false, width: "80px" },
    { name: "Category", selector: (row) => row.category_name || "—", sortable: true },
    {
      name: "Expense Name",
      selector: (row) => row.staffs || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.staffs || "—"}</span>
        </div>
      )
    },
    {
      name: "Total Amount",
      selector: (row) => `Rs. ${parseFloat(row.amount || 0).toLocaleString()}`,
      sortable: true,
      width: "140px"
    },
    {
      name: "Paid",
      selector: (row) => `Rs. ${parseFloat(row.paid_amount || 0).toLocaleString()}`,
      sortable: true,
      width: "120px"
    },
    {
      name: "Pending",
      selector: (row) => `Rs. ${parseFloat(row.pending_amount || 0).toLocaleString()}`,
      sortable: true,
      width: "120px",
      cell: (row) => (
        <span className="font-bold text-orange-600">
          Rs. {parseFloat(row.pending_amount || 0).toLocaleString()}
        </span>
      )
    },
    {
      name: "Status",
      width: "120px",
      cell: (row) => {
        const pending = parseFloat(row.pending_amount || 0);
        const paid = parseFloat(row.paid_amount || 0);

        if (pending === 0 && paid > 0) {
          return (
            <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
              ✓ Paid
            </span>
          );
        } else if (paid > 0 && pending > 0) {
          return (
            <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-semibold rounded-full">
              ⏳ Partial
            </span>
          );
        } else {
          return (
            <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full">
              ⚠ Unpaid
            </span>
          );
        }
      }
    },
    { name: "Date", selector: (row) => row.date || "—", sortable: true },
    {
      name: "Actions",
      cell: (row) => (
        <div className="flex gap-1 flex-wrap">
          <button
            onClick={() => handleEdit(row)}
            className="p-2 text-white bg-blue-700 hover:bg-blue-800 rounded-md shadow-xl text-xs transition"
            title="Edit"
          >
            <i className="fa fa-edit"></i>
          </button>
          <button
            onClick={() => handleDelete(row.id)}
            className="p-2 text-white bg-yellow-500 hover:bg-yellow-800 rounded-md shadow-xl text-xs transition"
            title="Delete"
          >
            <i className="fa fa-trash"></i>
          </button>
          <button
            onClick={() => openPdfModal(row)}
            className="p-2 text-white bg-green-600 hover:bg-green-800 rounded-md shadow-xl text-xs transition"
            title="Print PDF"
          >
            <i className="fa fa-print"></i>
          </button>
          {parseFloat(row.pending_amount || 0) > 0 && (
            <button
              onClick={() => handlePayNow(row)}
              className="p-2 text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 rounded-md shadow-xl text-xs font-semibold transition"
              title={`Pay Rs. ${parseFloat(row.pending_amount || 0).toLocaleString()}`}
            >
              <i className="fa fa-money-bill-wave"></i>
            </button>
          )}
          <button
            onClick={() => {
              setTempTotal(parseFloat(row.pending_amount || 0));
              fetchPaymentDetails(row.id);
            }}
            className="p-2 text-white bg-purple-600 hover:bg-purple-700 rounded-md shadow-xl text-xs transition"
            title="Payment History"
          >
            <i className="fa fa-history"></i>
          </button>
        </div>
      ),
      width: "280px"
    },
  ];

  // Enhanced payment columns
  const paymentColumns = [
    {
      name: "S.No",
      selector: (row, idx) => idx + 1,
      sortable: false,
      width: "70px",
      center: true
    },
    {
      name: "Amount",
      selector: (row) => `₹${parseFloat(row.amount || 0).toLocaleString()}`,
      sortable: true,
      cell: (row) => (
        <span className="font-semibold text-green-600">
          ₹{parseFloat(row.amount || 0).toLocaleString()}
        </span>
      )
    },
    {
      name: "Remark",
      selector: (row) => row.remark || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{row.remark || "No remark"}</span>
        </div>
      )
    },
    {
      name: "Payment Type",
      selector: (row) => row.payment_type || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-sm">{row.payment_type || "—"}</span>
        </div>
      )
    },
    {
      name: "Date",
      selector: (row) => row.date || "—",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span>{row.date || "—"}</span>
        </div>
      )
    },
    {
      name: "Actions",
      cell: (rowData) => (
        <div className="flex gap-1">
          <button
            onClick={() => handleEditPayment(rowData)}
            className="p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded text-xs shadow-sm transition"
            title="Edit Payment"
          >
            <i className="fa fa-edit"></i>
          </button>
          <button
            onClick={() => handleDeletePayment(rowData.payment_id || rowData.id)}
            className="p-1.5 text-white bg-red-600 hover:bg-red-700 rounded text-xs shadow-sm transition"
            title="Delete Payment"
          >
            <i className="fa fa-trash"></i>
          </button>
        </div>
      ),
      width: "120px",
      center: true
    },
  ];

  // Theme-aware table styles
  const customStyles = {
    table: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        width: "100%",
        border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(226, 232, 240)",
      },
    },
    headCells: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        color: theme === "dark" ? "white" : "black",
        fontWeight: "bold",
      },
    },
    cells: {
      style: {
        color: theme === "dark" ? "white" : "black",
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        border: theme === "dark" ? "solid 1px rgb(63, 62, 62)" : "solid 1px rgb(255, 255, 255)",
      },
    },
    pagination: {
      style: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        borderTop: theme === "dark" ? "solid 1px white" : "solid 1px rgb(226, 232, 240)",
      },
      pageButtonsStyle: {
        backgroundColor: theme === "dark" ? "rgb(31, 41, 55)" : "white",
        color: theme === "dark" ? "white" : "black",
        fill: theme === "dark" ? "white" : "black",
        "&:hover": {
          backgroundColor: theme === "dark" ? "rgb(55, 65, 81)" : "rgb(226, 232, 240)",
        },
      },
    },
  };

  return (
    <div>
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-200 bg-opacity-50 z-50">
          <img src={engineGif} alt="Loading..." className="w-24 h-24" />
        </div>
      )}

      <div className={`md:ml-[250px] w-full md:w-[calc(100%-250px)] mt-16 h-[92vh] flex justify-center items-center box-border ${theme === "dark" ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-800"}`}>
        <div className={`w-[95%] md:w-[98%] h-[95%] px-4 py-2 rounded-xl shadow-xl overflow-auto ${theme === "dark" ? "bg-gray-800 text-gray-200" : "bg-white text-gray-800"}`}>

          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-2">
            <Head_data
              data="Manage Expenses"
              font=""
              size="3xl"
              color={theme === "dark" ? "text-gray-200" : "text-gray-800"}
            />
            <div className="flex w-full md:w-auto gap-2">
              <button
                className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-lg transition font-medium"
                onClick={() => {
                  setFormData({
                    id: null,
                    category_id: "",
                    amount: "",
                    date: getTodayDate(),
                    staffs: "",
                  });
                  setMode("add");
                  setModalOpen(true);
                }}
              >
                <i className="fa fa-plus mr-2"></i>Add Expense
              </button>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search by category or expense name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full md:w-96 px-4 my-2 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />

          {/* Main DataTable */}
          <DataTable
            columns={columns}
            data={Array.isArray(filteredExpenses) ? filteredExpenses.map((row, idx) => ({ ...row, sno: idx + 1 })) : []}
            pagination
            paginationPerPage={10}
            striped
            highlightOnHover
            customStyles={customStyles}
            noDataComponent={
              <div className="p-12 text-center">
                <i className="fa fa-receipt text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-500 mb-2">No expenses found</h3>
                <p className="text-gray-400">Add your first expense to get started</p>
              </div>
            }
          />

          {/* MAIN EXPENSE MODAL */}
          <Modal show={modalOpen} onClose={() => setModalOpen(false)} size="md">
            <Modal.Header>{mode === "add" ? "Add Expense" : "Edit Expense"}</Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    id="category"
                    value={formData.category_id}
                    onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  >
                    <option value="">Select Category</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="expense-name">Expense Name *</Label>
                  {(() => {
                    const categoryName = categories.find(c => c.id === Number(formData.category_id))?.category_name;
                    if (categoryName?.toLowerCase().includes("salary staff")) {
                      return (
                        <Select
                          id="expense-name"
                          value={formData.staffs}
                          onChange={(e) => setFormData({ ...formData, staffs: e.target.value })}
                        >
                          <option value="">Select Staff</option>
                          {staffList.map((staff) => (
                            <option key={staff.id} value={staff.name}>{staff.name}</option>
                          ))}
                        </Select>
                      );
                    } else {
                      return (
                        <div>
                          <input
                            list="expenseSuggestions"
                            type="text"
                            id="expense-name"
                            value={formData.staffs}
                            onChange={(e) => setFormData({ ...formData, staffs: e.target.value })}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                            placeholder="Enter expense name or select from suggestions"
                          />
                          <datalist id="expenseSuggestions">
                            {categoryExpenseNames.map((name, idx) => <option key={idx} value={name} />)}
                          </datalist>
                        </div>
                      );
                    }
                  })()}
                </div>
                <div>
                  <Label htmlFor="amount">Amount *</Label>
                  <TextInput
                    id="amount"
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Date *</Label>
                  <input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                    disabled={sessionStorage.getItem("position") !== "superadmin"}
                  />
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button_submit
                onClick={handleSubmit}
                data={`${mode === "add" ? "Submit" : "Update"}`}
                type="submit"
                className="ms-auto px-6"
              >
                {mode === "add" ? "Submit" : "Update"}
              </Button_submit>
            </Modal.Footer>
          </Modal>

          {/* ENHANCED PAYMENT MODAL */}
          <Modal show={paymentModalOpen} onClose={() => setPaymentModalOpen(false)} size="lg">
            <Modal.Header>
              <div className="flex items-center gap-3">
                <i className="fa fa-money-bill-wave text-2xl text-green-600"></i>
                <div>
                  <h3 className="text-xl font-bold">Make Payment</h3>
                  <p className="text-sm text-gray-500">{selectedExpense?.staffs} - {selectedExpense?.category_name}</p>
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-6">
                {/* Expense Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl border">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Expense ID</p>
                      <p className="font-bold text-lg">{selectedExpense?.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
                      <p className="font-bold text-xl text-blue-600">
                        ₹{parseFloat(selectedExpense?.amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Paid So Far</p>
                      <p className="font-bold text-xl text-green-600">
                        ₹{parseFloat(selectedExpense?.paid_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Pending</p>
                      <p className="font-bold text-2xl text-orange-600 bg-orange-100 px-3 py-1 rounded-full inline-block">
                        ₹{parseFloat(selectedExpense?.pending_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Form */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="payment-amount" className="text-lg font-semibold">Payment Amount *</Label>
                    <TextInput
                      id="payment-amount"
                      type="number"
                      value={paymentFormData.amount}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, amount: e.target.value })}
                      placeholder="Enter payment amount"
                      className="text-2xl py-4"
                      helperText={
                        paymentFormData.amount && parseFloat(paymentFormData.amount) > parseFloat(selectedExpense?.pending_amount || 0)
                          ? "⚠️ Amount exceeds pending balance"
                          : `Maximum allowed: ₹${parseFloat(selectedExpense?.pending_amount || 0).toLocaleString()}`
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="payment-date">Payment Date</Label>
                    <input
                      id="payment-date"
                      type="date"
                      value={paymentFormData.date}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, date: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                      max={getTodayDate()}
                      disabled={sessionStorage.getItem("position") !== "superadmin"}
                    />
                  </div>

                  <div>
                    <Label htmlFor="payment-type">Payment Type</Label>
                    <select
                      id="payment-type"
                      value={paymentFormData.payment_type}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, payment_type: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600"
                    >
                      <option value="">Select Payment Type</option>
                      <option value="Cash">💵 Cash</option>
                      <option value="UPI">📱 UPI</option>
                      <option value="Bank">🏦 Bank Transfer</option>
                      <option value="Credit">💳 Credit Card</option>
                      <option value="Cheque">📄 Cheque</option>
                      <option value="Other">📋 Other</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="payment-remark">Remark (Optional)</Label>
                    <textarea
                      id="payment-remark"
                      value={paymentFormData.remark}
                      onChange={(e) => setPaymentFormData({ ...paymentFormData, remark: e.target.value })}
                      placeholder="Payment method (Cash, Bank Transfer, UPI, etc.)"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 resize-vertical"
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="flex justify-between w-full">
                <button
                  onClick={() => setPaymentModalOpen(false)}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePaymentSubmit}
                  disabled={!paymentFormData.amount || parseFloat(paymentFormData.amount) <= 0}
                  className="px-8 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition flex items-center gap-2 shadow-lg font-semibold"
                >
                  <i className="fa fa-check-circle"></i>
                  Record Payment
                </button>
              </div>
            </Modal.Footer>
          </Modal>

          {/* EDIT PAYMENT MODAL */}
          <Modal show={editPaymentModalOpen} onClose={() => setEditPaymentModalOpen(false)}>
            <Modal.Header>Edit Payment</Modal.Header>
            <Modal.Body>
              <div className="space-y-4">
                <div>
                  <Label>Payment ID</Label>
                  <TextInput value={editPaymentData.payment_id} disabled />
                </div>
                <div>
                  <Label>Amount</Label>
                  <TextInput
                    type="number"
                    value={editPaymentData.amount}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, amount: e.target.value })}
                    disabled
                  />
                </div>

                <div>
                  <Label>Remark</Label>
                  <TextInput
                    type="text"
                    value={editPaymentData.remark}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, remark: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <input
                    type="date"
                    value={editPaymentData.date}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, date: e.target.value })}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                <div>
                  <Label>Payment Type</Label>
                  <select
                    value={editPaymentData.payment_type}
                    onChange={(e) => setEditPaymentData({ ...editPaymentData, payment_type: e.target.value })}
                    className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="">Select Payment Type</option>
                    <option value="Cash">💵 Cash</option>
                    <option value="UPI">📱 UPI</option>
                    <option value="Bank">🏦 Bank Transfer</option>
                    <option value="Credit">💳 Credit Card</option>
                    <option value="Cheque">📄 Cheque</option>
                    <option value="Other">📋 Other</option>
                  </select>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button_submit
                onClick={handleUpdatePayment}
                data="Update Payment"
                className="ms-auto"
                disabled={editPaymentData.amount > temp_total}
              >
                Update Payment
              </Button_submit>
            </Modal.Footer>
          </Modal>

          {/* ENHANCED PAYMENT HISTORY MODAL */}
          <Modal show={paymentDetailsModalOpen} onClose={() => setPaymentDetailsModalOpen(false)} size="4xl">
            <Modal.Header>
              <div className="flex flex-col">
                <div className="flex items-center gap-3">
                  <i className="fa fa-history text-2xl text-purple-600"></i>
                  <span className="text-2xl font-bold">Payment History</span>
                </div>
                <p className="text-sm text-gray-500 ml-9">{selectedExpense?.staffs} - {selectedExpense?.category_name}</p>
              </div>
            </Modal.Header>
            <Modal.Body>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl">
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Amount</p>
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    ₹{parseFloat(selectedExpense?.amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Paid Amount</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    ₹{parseFloat(selectedExpense?.paid_amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Pending Amount</p>
                  <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    ₹{parseFloat(selectedExpense?.pending_amount || 0).toLocaleString()}
                  </p>
                </div>
                <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border">
                  <p className="text-sm text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Payments</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {paymentDetails.length}
                  </p>
                </div>
              </div>

              {/* Payment Table */}
              <div className="border rounded-xl overflow-hidden shadow-sm">
                <DataTable
                  columns={paymentColumns}
                  data={Array.isArray(paymentDetails) ? paymentDetails : []}
                  pagination
                  paginationPerPage={5}
                  customStyles={customStyles}
                  noDataComponent={
                    <div className="p-12 text-center">
                      <i className="fa fa-receipt text-6xl text-gray-300 mb-4"></i>
                      <h3 className="text-xl font-semibold text-gray-500 mb-2">No payments recorded</h3>
                      <p className="text-gray-400">
                        {parseFloat(selectedExpense?.pending_amount || 0) > 0
                          ? "Make the first payment to track here"
                          : "All payments completed!"
                        }
                      </p>
                    </div>
                  }
                />
              </div>
            </Modal.Body>
            <Modal.Footer>
              <button
                onClick={() => setPaymentDetailsModalOpen(false)}
                className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition font-medium"
              >
                Close
              </button>
              {parseFloat(selectedExpense?.pending_amount || 0) > 0 && (
                <button
                  onClick={() => {
                    setPaymentDetailsModalOpen(false);
                    handlePayNow(selectedExpense);
                  }}
                  className="px-6 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition font-medium shadow-lg flex items-center gap-2"
                >
                  <i className="fa fa-money-bill-wave"></i>
                  Make Payment
                </button>
              )}
            </Modal.Footer>
          </Modal>

          {/* PDF MODAL */}
          <Modal show={pdfpopup} size="6xl" popup={true} onClose={() => setPdfpopup(false)}>
            <Modal.Header className="bg-white text-gray-900 border-b border-gray-200"></Modal.Header>
            <Modal.Body className="flowbite-modal-b">
              <div id="pdf-content" className="border border-gray-500 mx-2 bg-white p-6 text-gray-800" style={{ minWidth: "700px" }}>
                <div className="flex items-center mb-6 pb-6 border-b-2 border-gray-300">
                  <div>
                    <img src={Logo} alt="JALS Automobiles Logo" className="h-20 w-auto" />
                  </div>
                  <div className="flex-1 text-center ml-8">
                    <h2 className="text-3xl font-bold uppercase tracking-wide mb-2">JALS AUTOMOBILES</h2>
                    {sessionStorage.getItem("branch") === "JBRANCH2025030" ? (
                      <>
                        <p className="text-lg mb-1">Door No: 6/63, Jals Road, Kuppottu Vilai,</p>
                        <p className="text-lg mb-1">Kadamacode, Melpuram, Pacode Post - 629168</p>
                        <p className="text-lg mb-2">Ph: 04651-264007 | Mob: 8300451644</p>
                        <p className="font-semibold text-lg">GSTIN: 33AMEPL9265E1Z1</p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg mb-1">6/31 F, Karavilagam Road, Chennithottam Junction,</p>
                        <p className="text-lg mb-1">Vettuvanni, Marthandam - 629162</p>
                        <p className="text-lg mb-2">Ph: 04651-271220 | Mob: 9488495820</p>
                        <p className="font-semibold text-lg">GSTIN: 33AMEPL9265E1Z1</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-8 pb-4 border-b-2 border-gray-300">
                  <h3 className="text-2xl font-bold tracking-wide">EXPENSE RECEIPT</h3>
                  <div className="text-right">
                    <p className="text-lg">
                      <span className="font-bold">Date: </span>
                      <span className="font-semibold">{pdf_data?.date || ""}</span>
                    </p>
                  
                  </div>
                </div>

                <div className="mb-8 p-6 bg-gray-50 rounded-xl">
                  <h4 className="text-xl font-semibold mb-4">Expense Details:</h4>
                  <div className="grid grid-cols-2 gap-6 text-lg">
                    <div>
                      <p><strong>Category:</strong> {pdf_data?.category_name || "—"}</p>
                      <p><strong>Expense:</strong> {pdf_data?.staffs || "—"}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-green-600">
                        ₹{parseFloat(pdf_data?.amount || 0).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {parseFloat(pdf_data?.pending_amount || 0) > 0
                          ? `Pending: ₹${parseFloat(pdf_data?.pending_amount || 0).toLocaleString()}`
                          : "Fully Paid"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-end mt-12 pt-8 border-t-2 border-gray-300">
                  <div>
                    <p className="font-bold text-lg mb-4">Prepared By</p>
                    <p className="text-gray-500">JALS Automobiles</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-lg mb-8">Authorized Signatory</p>
                    <p className="text-gray-500 text-sm">Accounts Department</p>
                  </div>
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer className="flowbite-modal-b">
              <button
                onClick={handlePrint}
                className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition shadow-xl font-semibold text-lg"
              >
                <i className="fa fa-download mr-2"></i>Download PDF
              </button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Expenses;
